/**
 * The values `score_persona_category_result.overallScore` holds: a lead's scoring verdict. It is a
 * plain string column — the tiers as digits, the two non-tiers as words.
 *
 * - `"1"` / `"2"` / `"3"` — P1 / P2 / P3.
 * - `"Disqualified"` — scoring looked at the lead and it does not fit.
 * - `"Unsure"` — scoring could not judge the lead: a (segment × persona) cell with no criteria, a
 *   company it could not classify, an investor no persona accepts. Rendered "?", with the reason as
 *   its tooltip. It never counts as qualified, and it stays in an "all leads" campaign (PREC-4075).
 *
 * Before PREC-4075 these were ad-hoc string lists in half a dozen places, and a value added to one
 * was missing from the next filter. Every filter, count and label that branches on the verdict
 * reads its values from here.
 */
export const LEAD_OVERALL_SCORE = {
	P1: "1",
	P2: "2",
	P3: "3",
	DISQUALIFIED: "Disqualified",
	UNSURE: "Unsure",
} as const;

export type LeadOverallScore = (typeof LEAD_OVERALL_SCORE)[keyof typeof LEAD_OVERALL_SCORE];

/** The tiers that count as qualified, best first. */
export const QUALIFIED_LEAD_OVERALL_SCORES: readonly LeadOverallScore[] = [
	LEAD_OVERALL_SCORE.P1,
	LEAD_OVERALL_SCORE.P2,
	LEAD_OVERALL_SCORE.P3,
];

/** Every stored verdict, best first. */
export const LEAD_OVERALL_SCORES: readonly LeadOverallScore[] = [
	...QUALIFIED_LEAD_OVERALL_SCORES,
	LEAD_OVERALL_SCORE.DISQUALIFIED,
	LEAD_OVERALL_SCORE.UNSURE,
];
